import { API_BASE_URL, performTokenRefresh } from "@repo/api";
import type { HostConfig } from "./hostConfig";

interface PermissionParams {
  isReadOnly?: boolean;
  permissions?: string[];
  userRole?: string;
  license?: HostConfig["license"];
}

export interface IspmHostConfig extends HostConfig {
  assetBaseUrl?: string;
  isServiceEnabled?: boolean;
  homeUrl?: string;
  disableIntroModal?: boolean;
  disablePermissionsApi?: boolean;
  enabledWidgetIds?: string[];
  lockGovernanceTab?: boolean;
  hideDashboardHeader?: boolean;
  hideCustomInsights?: boolean;
  lockWorkforceCertificationWidgets?: boolean;
  lockExternalTab?: boolean;
  hideIdentitiesAllAndSummary?: boolean;
  disableTimelineAndComparison?: boolean;
  showRiskyAccess?: boolean;
  disableCopilot?: boolean;
  /** When true, the host app manages the loading indicator — ispm-ui
   *  suppresses its own internal loaders during initial data fetch. */
  hostManagesLoading?: boolean;
  unusedActiveServiceAccounts?: boolean;
  redirectToPosturePolicies?: boolean;
  addUsernameHeader?: boolean;
  hideManageRuleButton?: boolean;
  loadDiscoveryRules?: boolean;
  hostName: string;
}

const isDev = import.meta.env.DEV;

export function getIspmHostConfig(
  onNavigate?: (path: string) => void,
  permissionParams?: PermissionParams,
): IspmHostConfig {
  const apiBaseUrl = isDev
    ? `${window.location.origin}/api-proxy`
    : (import.meta.env.VITE_API_BASE_URL as string) || window.location.origin;

  // Always use a relative path so the asset URL resolves against the current
  // origin. This ensures each environment (dev, beta, prod) fetches style.css
  // from its own deployment bucket rather than a hardcoded gateway domain.
  const assetBaseUrl = "/mfe/ispm-ui";

  return {
    basename: "/dashboard",
    apiBaseUrl,
    assetBaseUrl,
    isServiceEnabled: true,
    homeUrl: "/",
    disableIntroModal: true,
    disablePermissionsApi: true,
    enabledWidgetIds: [
      "dormant_accounts",
      "orphan_accounts",
      "active_users_with_inactive_mangers",
    ],
    lockGovernanceTab: true,
    hideDashboardHeader: true,
    hideCustomInsights: true,
    lockWorkforceCertificationWidgets: true,
    lockExternalTab: true,
    hideIdentitiesAllAndSummary: true,
    disableTimelineAndComparison: true,
    showRiskyAccess: true,
    disableCopilot: true,
    hostManagesLoading: true,
    redirectToPosturePolicies: true,
    unusedActiveServiceAccounts: true,
    addUsernameHeader: true,
    hideManageRuleButton: false,
    loadDiscoveryRules: true,
    hostName: "standalone-ui",
    onUnauthorized: () => performTokenRefresh(API_BASE_URL),
    onNavigate,
    ...permissionParams,
  };
}
